import { useRBAC } from "@bcc/contexts/rbac-context";
import { ROUTE_PATHS } from "@bcc/lib/constants";
import type { AccessibleModule } from "@shared/types";
import type React from "react";
import { Navigate } from "react-router-dom";
import { TextLoader } from "../ui/text-loader/text-loader";

interface ProtectedRouteProps {
  /** Content to render if permission check passes */
  readonly children: React.ReactNode;
  /** Required page permission (e.g., "orders.view", "batch.create") */
  readonly requiredPage?: string;
  /** Required module in menu groups (e.g., "orders", "batch") */
  readonly requiredModule?: AccessibleModule;
  /** Path to redirect to if access is denied (default: home) */
  readonly fallbackPath?: string;
}

/**
 * Route guard that redirects users without required permissions.
 *
 * @example
 * ```tsx
 * // In route definition
 * <Route
 *   path="/orders"
 *   element={
 *     <ProtectedRoute requiredPage="orders.view" requiredModule="orders">
 *       <OrdersPage />
 *     </ProtectedRoute>
 *   }
 * />
 *
 * // With custom fallback
 * <Route
 *   path="/batches/create"
 *   element={
 *     <ProtectedRoute
 *       requiredPage="batch.create"
 *       fallbackPath="/batches"
 *     >
 *       <BatchCreatePage />
 *     </ProtectedRoute>
 *   }
 * />
 * ```
 */
export function ProtectedRoute({ children, requiredPage, requiredModule, fallbackPath = ROUTE_PATHS.HOME }: Readonly<ProtectedRouteProps>) {
  const { hasPageAccess, hasModuleAccess, isLoading } = useRBAC();

  // Show loading state while permissions are being fetched
  if (isLoading) {
    return <TextLoader />;
  }

  // Check module access
  if (requiredModule && !hasModuleAccess(requiredModule)) {
    return <Navigate to={fallbackPath} replace />;
  }

  // Check specific page access
  if (requiredPage && !hasPageAccess(requiredPage)) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
}
